import { useEffect } from 'react';
import type { ErrorContext } from './report';

export type { ErrorContext } from './report';

/**
 * The entry-side half of error reporting: a few hundred bytes that notice an error and load the
 * reporter (`./report`, scrubbing, de-duplication, transports) only when there is one to report.
 * Nothing here talks to the network by itself.
 */

const loadReporter = () => import('./report');

/** Report an error you caught yourself. Safe to call from anywhere; never throws; reports each error once. */
export function reportError(error: unknown, context?: ErrorContext): void {
  void loadReporter().then(
    (reporter) => {
      reporter.reportErrorNow(error, context);
    },
    () => undefined, // the chunk could not load (offline, a new deploy): there is nothing to add
  );
}

/**
 * Global handlers: uncaught exceptions (`error`) and promises nobody handled (`unhandledrejection`).
 * (React 19 also routes an error no boundary caught to `window.onerror`, so it arrives here too.)
 * Returns the function that removes them.
 */
export function installErrorReporting(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onError = (event: ErrorEvent): void => {
    void loadReporter().then(
      (reporter) => {
        reporter.reportWindowError(event);
      },
      () => undefined,
    );
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    void loadReporter().then(
      (reporter) => {
        reporter.reportRejection(event);
      },
      () => undefined,
    );
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/** Mount once, in the providers: installs the global handlers for the life of the page. */
export function useErrorReporting(): void {
  useEffect(() => installErrorReporting(), []);
}
